import type { ApiError, ErrorCode, FieldIssue } from '@hm/contracts'
import { translate } from './i18n'

/**
 * Thin typed fetch wrapper.
 *
 * Everything is same-origin and relative, so the client is identical whether the
 * Worker serves it or a future dedicated backend does — nothing here hardcodes a
 * host.
 */

export class ApiRequestError extends Error {
  readonly code: ErrorCode
  readonly fields: FieldIssue[]
  readonly status: number
  readonly retryAfter: number | undefined

  constructor(status: number, body: ApiError['error']) {
    super(body.message)
    this.name = 'ApiRequestError'
    this.status = status
    this.code = body.code
    this.fields = body.fields ?? []
    this.retryAfter = body.retryAfter
  }

  /** Message for a specific form field, if the server blamed one. */
  fieldError(path: string): string | undefined {
    return this.fields.find((f) => f.path === path)?.message
  }
}

type Query = Record<string, string | number | boolean | null | undefined>

async function request<T>(
  method: string,
  path: string,
  options: {
    body?: unknown
    /** Sent verbatim with the caller's own content type. Used for CSV upload. */
    rawBody?: string
    query?: Query
    headers?: Record<string, string>
  } = {},
): Promise<T> {
  const url = new URL(path, window.location.origin)
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }

  const headers: Record<string, string> = { ...options.headers }
  if (options.body) headers['Content-Type'] = 'application/json'

  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: options.rawBody ?? (options.body ? JSON.stringify(options.body) : undefined),
  })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const parsed: unknown = text ? JSON.parse(text) : null

  if (!res.ok) {
    const body = (parsed as ApiError | null)?.error
    throw new ApiRequestError(
      res.status,
      body ?? { code: 'internal', message: translate('common.something_went_wrong') },
    )
  }

  return parsed as T
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  /**
   * `headers` exists for `Idempotency-Key` on the calls that create money or move
   * stock. Nothing else should need it — see packages/http/src/idempotency.ts.
   */
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('POST', path, { body, headers }),
  /** A body that is not JSON — today, a CSV file a shop is uploading. */
  postRaw: <T>(path: string, rawBody: string, contentType: string) =>
    request<T>('POST', path, { rawBody, headers: { 'Content-Type': contentType } }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, { body }),
}

/** Field errors from the server, keyed by path, for react-hook-form's setError. */
export function fieldErrorsOf(error: unknown): Record<string, string> {
  if (!(error instanceof ApiRequestError)) return {}
  return Object.fromEntries(error.fields.map((f) => [f.path, f.message]))
}

export function messageOf(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof Error) return error.message
  return translate('common.something_went_wrong')
}
